import { m } from "@nerves/i18n/messages";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { type FormEvent, useState } from "react";
import { Button, Callout, TextField } from "@/components/ui";
import { api } from "@/lib/api";
import { signIn } from "@/lib/auth-client";
import { authConfigQuery, keys, loadMe } from "@/lib/queries";

interface LoginSearch {
  redirect?: string;
  error?: string;
}

/** Only same-app paths: never bounce to another origin after sign-in. */
function safeTarget(value: string | undefined): string {
  return value?.startsWith("/") && !value.startsWith("//") ? value : "/";
}

export const Route = createFileRoute("/login")({
  validateSearch: (s: Record<string, unknown>): LoginSearch => ({
    redirect: typeof s.redirect === "string" ? s.redirect : undefined,
    error: typeof s.error === "string" ? s.error : undefined,
  }),
  beforeLoad: async ({ context, search }) => {
    if (await loadMe(context.queryClient)) throw redirect({ to: safeTarget(search.redirect) });
  },
  component: Login,
});

function DevLogin({ target }: { target: string }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [failed, setFailed] = useState(false);
  const [pending, setPending] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setPending(true);
    setFailed(false);
    const res = await signIn.email({ email, password });
    setPending(false);
    if (res.error) {
      setFailed(true);
      return;
    }
    await qc.invalidateQueries({ queryKey: keys.me });
    void navigate({ to: target });
  };
  return (
    // noValidate + validationBehavior="aria": no browser bubbles, our own inline messages.
    <form noValidate onSubmit={submit} className="flex flex-col gap-3 border-t border-line pt-5">
      <h2 className="text-sm font-medium text-fg-muted">{m.login_dev()}</h2>
      <TextField
        label={m.login_email()}
        type="email"
        autoComplete="email"
        value={email}
        onChange={setEmail}
        isRequired
        validationBehavior="aria"
      />
      <TextField
        label={m.login_password()}
        type="password"
        autoComplete="current-password"
        value={password}
        onChange={setPassword}
        isRequired
        validationBehavior="aria"
      />
      {failed ? (
        <Callout tone="danger" role="alert">
          {m.login_failed()}
        </Callout>
      ) : null}
      <Button type="submit" isPending={pending}>
        {m.login_submit()}
      </Button>
    </form>
  );
}

function Login() {
  const search = Route.useSearch();
  const target = safeTarget(search.redirect);
  const config = useQuery(authConfigQuery).data;
  const [pending, setPending] = useState(false);
  const [ssoFailed, setSsoFailed] = useState(false);
  return (
    <main id="main" tabIndex={-1} className="flex min-h-dvh items-center justify-center bg-chrome px-4 py-10">
      <div className="flex w-full max-w-sm flex-col gap-6 rounded-panel border border-canvas-border bg-canvas p-6 shadow-(--canvas-shadow)">
        <div className="flex flex-col gap-2">
          <span
            aria-hidden="true"
            className="flex size-9 items-center justify-center rounded-control bg-primary text-sm font-semibold text-primary-fg"
          >
            N
          </span>
          <h1 className="text-xl font-semibold tracking-tight">{m.login_title()}</h1>
          <p className="text-sm text-fg-muted">{m.login_tagline()}</p>
        </div>
        {search.error || ssoFailed ? (
          <Callout tone="danger" role="alert">
            {/* Better Auth reports a refused profile (our group check returns no user) as missing user info. */}
            {search.error && /user_info|unable_to_get_user/i.test(search.error)
              ? m.login_no_access()
              : m.login_sso_failed({ code: search.error ?? "network" })}
          </Callout>
        ) : null}
        {config?.oidc ? (
          <Button
            variant="primary"
            size="lg"
            isPending={pending}
            onPress={async () => {
              setPending(true);
              setSsoFailed(false);
              try {
                const res = await api.post<{ url?: string }>("/api/auth/sign-in/social", {
                  provider: config.oidc!.providerId,
                  callbackURL: target,
                  errorCallbackURL: "/login",
                });
                if (res.url) window.location.assign(res.url);
                else setSsoFailed(true);
              } catch {
                setSsoFailed(true);
              } finally {
                setPending(false);
              }
            }}
          >
            {m.login_with({ name: config.oidc.name })}
          </Button>
        ) : config ? (
          <Callout tone="warning">{m.login_not_configured()}</Callout>
        ) : null}
        {config?.devLogin ? <DevLogin target={target} /> : null}
      </div>
    </main>
  );
}
